import React, { useEffect, useRef } from "react";
import { createGlobalStyle } from "styled-components";

const GlobalCursorStyle = createGlobalStyle`
    *, *::before, *::after {
        cursor: none !important;
        user-select: none;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
    }

    /* Restore default cursor and selection on touch/pointer:coarse devices */
    @media (hover: none), (pointer: coarse) {
        *, *::before, *::after {
            cursor: auto !important;
            user-select: auto;
            -webkit-user-select: auto;
        }
    }

    /* Re-enable text selection inside readable content */
    article p,
    article li,
    article h1,
    article h2,
    article h3,
    article h4,
    article blockquote,
    article code,
    article pre,
    .allow-select {
        user-select: text;
        -webkit-user-select: text;
        -moz-user-select: text;
        -ms-user-select: text;
    }
`;

const CursorManager = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        if (typeof window === "undefined") return;

        // ── Touch/coarse-pointer detection ────────────────────────────────────
        // Skip the custom cursor entirely on touch devices.
        // matchMedia("(hover: none)") covers phones + tablets.
        // matchMedia("(pointer: coarse)") covers stylus/touch screens.
        // We also listen for the first touchstart as a runtime fallback
        // for browsers that don't report hover/pointer correctly.
        const isTouch =
            window.matchMedia("(hover: none)").matches ||
            window.matchMedia("(pointer: coarse)").matches;

        if (isTouch) return; // bail — CSS already handles cursor:auto above

        const canvas = canvasRef.current;
        if (!canvas) return;

        let W = window.innerWidth;
        let H = window.innerHeight;
        canvas.width  = W;
        canvas.height = H;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        let mx = -200, my = -200;
        let mxEased = -200, myEased = -200;
        let isHovering = false;
        let isClicking = false;
        let animating  = true;
        const ripples  = [];

        const getIsDark = () => document.body.classList.contains("dark-mode");

        const color = (a) => getIsDark()
            ? `rgba(238,14,166,${a})`
            : `rgba(160,8,100,${a})`;

        const colorWhite = (a) => getIsDark()
            ? `rgba(255,120,210,${a})`
            : `rgba(200,10,130,${a})`;

        const draw = () => {
            if (!animating) return;
            requestAnimationFrame(draw);

            ctx.clearRect(0, 0, W, H);

            mxEased += (mx - mxEased) * 0.55;
            myEased += (my - myEased) * 0.55;

            const cx = mxEased;
            const cy = myEased;

            // ── Ripples ───────────────────────────────────────────────────────
            for (let i = ripples.length - 1; i >= 0; i--) {
                const r = ripples[i];
                r.t += 0.03;
                if (r.t >= 1) { ripples.splice(i, 1); continue; }

                const progress = r.t;
                const eased    = 1 - Math.pow(1 - progress, 3);
                const radius   = eased * 200;
                const alpha    = (1 - progress) * 0.65;

                ctx.strokeStyle = color(alpha);
                ctx.lineWidth   = 1.5 * (1 - progress * 0.5);
                ctx.beginPath(); ctx.arc(r.x, r.y, radius, 0, Math.PI * 2); ctx.stroke();

                const innerProgress = Math.max(0, progress - 0.1);
                const innerEased    = 1 - Math.pow(1 - innerProgress, 3);
                const innerRadius   = innerEased * 110;
                const innerAlpha    = Math.max(0, (1 - innerProgress) * 0.35);

                ctx.strokeStyle = color(innerAlpha);
                ctx.lineWidth   = 0.8;
                ctx.beginPath(); ctx.arc(r.x, r.y, innerRadius, 0, Math.PI * 2); ctx.stroke();

                if (progress < 0.15) {
                    const flashA = (0.15 - progress) / 0.15 * 0.8;
                    ctx.beginPath(); ctx.arc(r.x, r.y, 6, 0, Math.PI * 2);
                    ctx.fillStyle = colorWhite(flashA); ctx.fill();
                }
            }

            // ── Crosshair ─────────────────────────────────────────────────────
            if (cx < -100) return;

            const sz      = isHovering ? 24 : 20;
            const gap     = isHovering ? 6  : 5;
            const opacity = isClicking ? 0.5 : 1.0;

            // Outer circle
            ctx.beginPath();
            ctx.arc(cx, cy, isHovering ? 28 : 22, 0, Math.PI * 2);
            ctx.strokeStyle = color(0.18 * opacity);
            ctx.lineWidth   = 1.2;
            ctx.stroke();

            // Arms
            ctx.strokeStyle = color(0.95 * opacity);
            ctx.lineWidth   = 2.0;
            ctx.lineCap     = "round";

            ctx.beginPath(); ctx.moveTo(cx - sz, cy); ctx.lineTo(cx - gap, cy); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(cx + gap, cy); ctx.lineTo(cx + sz, cy); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(cx, cy - sz); ctx.lineTo(cx, cy - gap); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(cx, cy + gap); ctx.lineTo(cx, cy + sz); ctx.stroke();

            // Center dot
            ctx.beginPath(); ctx.arc(cx, cy, isHovering ? 3.5 : 3.0, 0, Math.PI * 2);
            ctx.fillStyle = color(0.98 * opacity); ctx.fill();

            // Hover corner ticks
            if (isHovering) {
                const tickLen = 8;
                const tickOff = gap + sz + 5;
                ctx.strokeStyle = color(0.65);
                ctx.lineWidth   = 1.5;
                [
                    [-tickOff, -tickOff,  tickLen, 0],
                    [-tickOff, -tickOff,  0,  tickLen],
                    [ tickOff, -tickOff, -tickLen, 0],
                    [ tickOff, -tickOff,  0,  tickLen],
                    [-tickOff,  tickOff,  tickLen, 0],
                    [-tickOff,  tickOff,  0, -tickLen],
                    [ tickOff,  tickOff, -tickLen, 0],
                    [ tickOff,  tickOff,  0, -tickLen],
                ].forEach(([ox, oy, dx, dy]) => {
                    ctx.beginPath();
                    ctx.moveTo(cx + ox, cy + oy);
                    ctx.lineTo(cx + ox + dx, cy + oy + dy);
                    ctx.stroke();
                });
            }
        };

        draw();

        const onMouseMove = (e) => {
            mx = e.clientX;
            my = e.clientY;
            const el = document.elementFromPoint(e.clientX, e.clientY);
            isHovering = el
                ? el.closest("a, button, [role='button'], input, textarea, select, label, [tabindex]") !== null
                : false;
        };
        const onClick      = (e) => { ripples.push({ x: e.clientX, y: e.clientY, t: 0 }); };
        const onMouseDown  = () => { isClicking = true;  };
        const onMouseUp    = () => { isClicking = false; };
        const onResize     = () => {
            W = window.innerWidth; H = window.innerHeight;
            canvas.width = W; canvas.height = H;
        };
        const onMouseLeave = () => { mx = -200; my = -200; };

        window.addEventListener("mousemove",  onMouseMove);
        window.addEventListener("click",      onClick);
        window.addEventListener("mousedown",  onMouseDown);
        window.addEventListener("mouseup",    onMouseUp);
        window.addEventListener("resize",     onResize);
        document.documentElement.addEventListener("mouseleave", onMouseLeave);

        return () => {
            animating = false;
            window.removeEventListener("mousemove",  onMouseMove);
            window.removeEventListener("click",      onClick);
            window.removeEventListener("mousedown",  onMouseDown);
            window.removeEventListener("mouseup",    onMouseUp);
            window.removeEventListener("resize",     onResize);
            document.documentElement.removeEventListener("mouseleave", onMouseLeave);
        };
    }, []);

    return (
        <>
            <GlobalCursorStyle />
            <canvas
                ref={canvasRef}
                aria-hidden="true"
                style={{
                    position:      "fixed",
                    top:           0,
                    left:          0,
                    width:         "100vw",
                    height:        "100vh",
                    pointerEvents: "none",
                    zIndex:        9999,
                }}
            />
        </>
    );
};

export default CursorManager;